import config from '../config';
import { mergeTurnStatusIntoMessages } from '../utils/turnStatus';

const buildUrl = (endpoint) => `${config.api.baseUrl}${endpoint}`;

// Conversation source — set once by the app based on how the chat was opened
// (inline_chat, widget, faq_chat, frontend, contact_form). Sent as the X-Source
// header on the first message so the backend records where the conversation came
// from. Null until set; the backend then defaults the column to 'unknown'.
let conversationSource = null;

/**
 * setConversationSource — called by App.js to tag all subsequent chat requests
 * with their origin. Pass null to clear.
 */
export const setConversationSource = (source) => {
    conversationSource = source || null;
};

/** Current X-Source value (inline_chat, frontend, demo, …). */
export const getConversationSource = () => conversationSource;

/** Build the X-Source header object, or an empty object when no source is set. */
const sourceHeader = () => (conversationSource ? { 'X-Source': conversationSource } : {});

/**
 * Resolve the website URL the chat originated from (stored in conversations.page_url).
 * This is the page/product context we later use for page/product-specific FAQ —
 * it is deliberately kept SEPARATE from `source` (which stays a clean enum).
 *
 * Resolution order:
 *   1. Explicit `?pageUrl=` / `?page_url=` query param — lets an embed snippet
 *      pass the true host-page URL into the iframe.
 *   2. document.referrer when we're inside an iframe — that's the parent page,
 *      since window.location would only be our own embed origin.
 *   3. window.location.href for normal top-level usage.
 */
const getOriginPageUrl = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const explicit = params.get('pageUrl') || params.get('page_url');
        if (explicit) return explicit;
        const inIframe = window.self !== window.top;
        if (inIframe && document.referrer) return document.referrer;
        return window.location.href;
    } catch (e) {
        // Cross-origin access to window.top can throw — fall back to our own URL.
        return window.location.href;
    }
};

/** Build the X-Page-Url header object with the resolved origin page URL. */
const pageUrlHeader = () => {
    const url = getOriginPageUrl();
    return url ? { 'X-Page-Url': url } : {};
};

// Optional page language header — set by the main app LanguageContext.
let pageLanguage = null;

export const setPageLanguage = (lang) => {
    pageLanguage = lang || null;
};

const pageLanguageHeader = () => (
    pageLanguage ? { 'X-Page-Language': pageLanguage } : {}
);

// Anonymous user identity — Safari ITP blocks third-party cookies when the
// frontend is embedded in an iframe on a merchant site. The backend sends
// user_id in stream_start; we persist it here and send X-Anonymous-Id on
// subsequent requests (same pattern as chatbot-widget/src/services/api.js).
const ANONYMOUS_ID_STORAGE_KEY = 'emilia_anonymous_id';
let freshAnonymousId = null;

const isFreshEmbedMode = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get('fresh') === 'true' || params.get('autostart') === 'true';
    } catch {
        return false;
    }
};

export const getAnonymousId = () => {
    if (isFreshEmbedMode()) return freshAnonymousId;
    try {
        return localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    } catch {
        return null;
    }
};

export const setAnonymousId = (id) => {
    if (isFreshEmbedMode()) {
        freshAnonymousId = id || null;
        return;
    }
    try {
        if (id) {
            localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, id);
        } else {
            localStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);
        }
    } catch {
        // localStorage unavailable (private browsing, storage full, etc.)
    }
};

const anonymousIdHeader = () => {
    const id = getAnonymousId();
    return id ? { 'X-Anonymous-Id': id } : {};
};

/**
 * Percent-encode a tenant-relative file path so it can be safely embedded in
 * an `/api/files/...` URL.
 *
 * The backend uses Flask's `<path:file_id>` converter which captures everything
 * after `/api/files/` including slashes — so path separators must stay as raw
 * `/` characters, while individual segments (folder names + filenames) need
 * standard URL-component encoding to handle umlauts, spaces, `?`, `#`, etc.
 *
 * Example: `my product/Spülmittel #1.pdf` → `my%20product/Sp%C3%BClmittel%20%231.pdf`
 */
const encodeFileId = (fileId) =>
    String(fileId)
        .split('/')
        .map(encodeURIComponent)
        .join('/');

/** Must match AppWrapper.js / backend RESERVED_SUBDOMAINS. */
const RESERVED_SUBDOMAINS = new Set([
    'admin', 'api', 'widget', 'app', 'www', 'staging',
]);

/**
 * Tenant slug for public asset URLs (img/a cannot send X-Tenant-Slug).
 * Mirrors the fetch interceptor in AppWrapper.js.
 */
export const resolveTenantSlugForApi = () => {
    try {
        const impersonate = sessionStorage.getItem('impersonate_tenant');
        if (impersonate) return impersonate;

        const hostname = window.location.hostname;
        if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
            const sub = hostname.split('.')[0];
            if (sub && !RESERVED_SUBDOMAINS.has(sub)) return sub;
        }

        const baseDomain = process.env.REACT_APP_BASE_DOMAIN;
        if (baseDomain && hostname.endsWith('.' + baseDomain)) {
            const sub = hostname.slice(0, -(baseDomain.length + 1));
            if (sub && !sub.includes('.') && !RESERVED_SUBDOMAINS.has(sub)) return sub;
        }

        return sessionStorage.getItem('custom_domain_slug');
    } catch {
        return null;
    }
};

export const chatService = {

    async sendMessage(message, conversationId = null) {
        try {
            const response = await fetch(buildUrl(config.endpoints.chat), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...sourceHeader(),
                    ...pageUrlHeader(),
                    ...pageLanguageHeader(),
                    ...anonymousIdHeader(),
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    message,
                    conversation_id: conversationId 
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (response.status === 202) {
                return this.pollForResponse(data.conversation_id);
            }

            if (!data || !data.final_answer) {
                throw new Error('Invalid response format from server');
            }

            const processedResponse = {
                response: data.final_answer,
                sources: data.sources || [],
                reasoning: data.reasoning || null,
                conversationId: data.conversation_id,
                message_id: data.message_id,
                debug: data.debug || null
            };
            return processedResponse;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Send a message with streaming response via SSE.
     * Instead of waiting for the full response, this streams the final_answer
     * content in real-time and delivers reasoning/sources at the end.
     * 
     * @param {string} message - The user's message
     * @param {string|null} conversationId - Existing conversation ID or null for new
     * @param {Object} callbacks - Event handlers for the stream
     * @param {Function} callbacks.onStart - Called with conversationId when stream starts
     * @param {Function} callbacks.onAckDelta - Called with text chunks as the LLM ack streams
     * @param {Function} callbacks.onAckDone - Called with {text, task} when ack stream completes
     * @param {Function} callbacks.onThinking - Called with {text, task} task-specific status
     * @param {Function} callbacks.onDelta - Called with text chunks as they arrive
     * @param {Function} callbacks.onDone - Called with {reasoning, sources, message_id, debug, conversationId}
     * @param {Function} callbacks.onError - Called with Error object on failure
     * @param {AbortSignal} callbacks.signal - AbortController signal to cancel the stream
     */
    async sendMessageStream(message, conversationId = null, callbacks = {}, retriedWithoutConversation = false) {
        const {
            onStart, onAckDelta, onAckDone, onThinking, onDelta, onAnswerComplete, onDone, onError, signal,
            // Optional validated attachment metadata from a prior /chat/attachments upload.
            attachments,
        } = callbacks;

        const body = {
            message,
            conversation_id: conversationId,
        };
        if (Array.isArray(attachments) && attachments.length > 0) {
            body.attachments = attachments;
        }
        
        try {
            const response = await fetch(buildUrl('/api/chat/stream'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...sourceHeader(),
                    ...pageUrlHeader(),
                    ...pageLanguageHeader(),
                    ...anonymousIdHeader(),
                },
                credentials: 'include',
                body: JSON.stringify(body),
                signal  // AbortController signal for cancellation
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errText = errorData.error || `HTTP error! status: ${response.status}`;
                // Stale conversation_id in sessionStorage + Safari-blocked user cookie
                // → instant 404 before SSE starts. Retry once as a new conversation.
                if (
                    response.status === 404
                    && conversationId
                    && !retriedWithoutConversation
                    && /conversation not found/i.test(String(errText))
                ) {
                    return this.sendMessageStream(message, null, callbacks, true);
                }
                throw new Error(errText);
            }

            // Read SSE stream via ReadableStream API (can't use EventSource with POST)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';        // Buffer for incomplete SSE events
            let receivedDone = false;  // Track if we got a proper 'done' event

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                // Decode the chunk and add to buffer
                buffer += decoder.decode(value, { stream: true });

                // SSE events are separated by double newlines (\n\n)
                // Split on that boundary and keep the last (possibly incomplete) part
                const events = buffer.split('\n\n');
                buffer = events.pop();  // Keep incomplete event in buffer

                // Parse each complete event block
                for (const eventBlock of events) {
                    if (!eventBlock.trim()) continue;
                    
                    let eventType = null;
                    let eventData = null;
                    
                    // Parse event: and data: lines from the SSE block
                    const lines = eventBlock.split('\n');
                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            eventType = line.slice(7).trim();
                        } else if (line.startsWith('data: ')) {
                            eventData = line.slice(6);
                        }
                    }
                    
                    // Process the parsed event
                    if (eventType && eventData) {
                        try {
                            const parsedData = JSON.parse(eventData);
                            
                            switch (eventType) {
                                case 'stream_start':
                                    if (parsedData.user_id) setAnonymousId(parsedData.user_id);
                                    if (onStart) onStart(parsedData);
                                    break;
                                case 'ack_delta':
                                    if (onAckDelta) onAckDelta(parsedData.text);
                                    break;
                                case 'ack_done':
                                    if (onAckDone) onAckDone(parsedData);
                                    break;
                                case 'thinking':
                                    if (onThinking) onThinking(parsedData);
                                    break;
                                case 'content_delta':
                                    // Append this text chunk to the displayed message
                                    if (onDelta) onDelta(parsedData.text);
                                    break;
                                case 'answer_complete':
                                    if (onAnswerComplete) onAnswerComplete(parsedData);
                                    break;
                                case 'done':
                                    // Stream complete - deliver all metadata
                                    receivedDone = true;
                                    if (onDone) onDone({
                                        final_answer: parsedData.final_answer || '',
                                        reasoning: parsedData.reasoning || null,
                                        sources: parsedData.sources || [],
                                        conversationId: parsedData.conversation_id,
                                        message_id: parsedData.message_id,
                                        debug: parsedData.debug || null,
                                        // Quick-reply chips (return reasons, ja/nein, …)
                                        options: Array.isArray(parsedData.options)
                                          ? parsedData.options
                                          : null,
                                        links: Array.isArray(parsedData.links)
                                          ? parsedData.links
                                          : null,
                                        order: parsedData.order || null,
                                    });
                                    break;
                                case 'error':
                                    // Server-side error during streaming
                                    if (onError) onError(new Error(parsedData.error));
                                    break;
                                default:
                                    break;
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }

            // If the stream ended without a 'done' event, something went wrong
            if (!receivedDone) {
                if (onError) onError(new Error('Stream ended unexpectedly without completion'));
            }
        } catch (error) {
            // Pass error to callback if provided, otherwise re-throw
            if (onError) onError(error);
            else throw error;
        }
    },

    async pollForResponse(conversationId, maxAttempts = 20) {
        const pollInterval = 3000; // 3 seconds
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/latest`), {
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                if (data.status === 'completed') {
                    return {
                        response: data.final_answer,
                        sources: data.sources || [],
                        reasoning: data.reasoning || null,
                        conversationId: conversationId,
                        message_id: data.message_id,
                        debug: data.debug || null
                    };
                }
                
                // If still processing, wait before next attempt
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                
            } catch (error) {
                throw error;
            }
        }
        
        throw new Error('Request timed out after maximum polling attempts');
    },

    async getConversations() {
        try {
            const response = await fetch(buildUrl(config.endpoints.conversations), {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch conversations');
            const data = await response.json();
            return data.conversations || [];
        } catch (error) {
            return [];
        }
    },

    async getConversationHistory(conversationId) {
        try {
            const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/messages`), {
                method: 'GET',
                headers: {
                    ...anonymousIdHeader(),
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const messages = await response.json();

            // First pass: build the mapped messages
            const mapped = messages.map(msg => {
                // For assistant messages, try to parse the content if it's a JSON string
                if (msg.role === 'assistant') {
                    try {
                        const parsedContent = JSON.parse(msg.content);
                        const isAdmin = msg.author_type === 'admin' || parsedContent.is_admin_reply;
                        return {
                            role: msg.role,
                            content: parsedContent.final_answer,
                            sources: isAdmin ? [] : (Array.isArray(parsedContent.sources) 
                                ? parsedContent.sources 
                                : (typeof parsedContent.sources === 'string' 
                                    ? parsedContent.sources.split('\n')
                                        .filter(line => line.trim())
                                        .map(line => line.trim())
                                        .filter(line => line.startsWith('-'))
                                        .map(line => line.substring(1).trim()) 
                                    : [])),
                            reasoning: isAdmin ? null : (parsedContent.reasoning || null),
                            message_id: msg.message_id,
                            debug: isAdmin ? null : (msg.debug || parsedContent.debug || null),
                            author_type: isAdmin ? 'admin' : undefined,
                            admin_author: msg.admin_author || parsedContent.admin_author || null,
                            // Quick-reply chips persisted on clarification messages
                            options: Array.isArray(parsedContent.options)
                              ? parsedContent.options
                              : null,
                            links: Array.isArray(parsedContent.links)
                              ? parsedContent.links
                              : null,
                        };
                    } catch (e) {
                        // If not JSON, treat it as plain text
                        return {
                            role: msg.role,
                            content: msg.content,
                            sources: [],
                            reasoning: null,
                            message_id: msg.message_id,
                            debug: msg.debug || null,
                            author_type: msg.author_type,
                            admin_author: msg.admin_author || null,
                        };
                    }
                }
                // For user messages, carry lead_debug + attachments through
                return {
                    role: msg.role,
                    content: msg.content,
                    message_id: msg.message_id,
                    debug: msg.debug || null,
                    lead_debug: msg.lead_debug || null,
                    attachments: Array.isArray(msg.attachments) ? msg.attachments : null,
                };
            });

            // Second pass: attach each user message's lead_debug to the
            // following assistant message's debug object so it renders in the
            // debug panel alongside the rest of the debug info.
            for (let i = 0; i < mapped.length - 1; i++) {
                if (mapped[i].role === 'user' && mapped[i].lead_debug && mapped[i + 1].role === 'assistant') {
                    if (!mapped[i + 1].debug) mapped[i + 1].debug = {};
                    mapped[i + 1].debug.lead_extraction = mapped[i].lead_debug;
                }
            }

            return mergeTurnStatusIntoMessages(mapped);
        } catch (error) {
            throw error;
        }
    },

    async deleteConversation(conversationId) {
        const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/hide`), {
            method: 'POST',
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to delete conversation');
        return response.json();
    },

    async generateTitle(conversationId) {
        const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/title`), {
            method: 'POST',
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to generate title');
        return response.json();
    },

    async get(url) {
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async post(url, data) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async getSystemPrompt() {
        return await this.getSetting('system-prompt');
    },

    async updateSystemPrompt(prompt) {
        return await this.updateSetting('system-prompt', prompt);
    },

    async getSetting(key) {
        try {
            const response = await this.get(buildUrl(`${config.endpoints.settings}/${key}`));
            return response.value;
        } catch (error) {
            if (error.message.includes('not found')) {
                return null;
            }
            throw error;
        }
    },

    async updateSetting(key, value) {
        return this.post(buildUrl(`${config.endpoints.settings}/${key}`), { value });
    },


    async updateVectorStoreSettings(prompt, vectorstoreId) {
        await Promise.all([
            this.updateSetting('vector-store-prompt', prompt),
            this.updateSetting('vectorstore-id', vectorstoreId)
        ]);
    },

    async updateProductClassifierSettings(prompt, threshold) {
        await Promise.all([
            this.updateSetting('product-classifier-prompt', prompt),
            this.updateSetting('product-classifier-threshold', threshold)
        ]);
    },

    async getVectorClassifierSetting(key) {
        return this.getSetting(`vector-classifier-${key}`);
    },

    async updateVectorClassifierSetting(key, value) {
        return this.updateSetting(`vector-classifier-${key}`, value);
    },

    async getLeads() {
        try {
            const response = await fetch(buildUrl(config.endpoints.leads), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to fetch leads');
            }
            const result = await response.json();
            return result;  
        } catch (error) {
            return { data: [], error: error.message };
        }
    },

    async userInfo() {
        try {
            const response = await fetch(buildUrl(config.endpoints.userInfo), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch user data: ${response.status}`);
            }
            return response.json();
        } catch (error) {
            throw error;
        }
    },

    async getProfile() {
        const response = await fetch(buildUrl(config.endpoints.profile), {
            method: 'GET',
            credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load profile');
        return data;
    },

    async updateProfile({ name, job_title }) {
        const response = await fetch(buildUrl(config.endpoints.profile), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name, job_title }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to update profile');
        return data;
    },

    async uploadProfileAvatar(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(buildUrl(config.endpoints.profileAvatar), {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to upload avatar');
        return data;
    },

    async deleteLead(leadId) {
        const response = await fetch(`${config.api.baseUrl}${config.endpoints.leads}/${leadId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include'
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete lead');
        }
    },

    async sendFeedback(messageId, conversationId, isPositive, feedbackType = null, feedbackText = null, isAdminFeedback = false) {
        try {
            const body = {
                message_id: messageId,
                conversation_id: conversationId,
                is_positive: isPositive,
                feedback_type: feedbackType,
                feedback_text: feedbackText,
            };
            // Only include the admin flag when explicitly set — non-admin users
            // can safely send it; the server forces it to false for non-admins.
            if (isAdminFeedback) {
                body.is_admin_feedback = true;
            }
            const response = await fetch(buildUrl('/api/feedback'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...anonymousIdHeader(),
                },
                credentials: 'include',
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackStatus(messageIds) {
        try {
            const response = await fetch(buildUrl(`/api/feedback/status?message_ids=${messageIds}`), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackWithContext() {
        try {
            const response = await fetch(buildUrl('/api/feedback/with-context'), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackStats(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/feedback/stats?${params}`), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getEventStats(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/events/stats?${params}`), {
                method: 'GET', credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch event stats');
            return await response.json();
        } catch (error) {
            return {};
        }
    },

    async getDailyEventCounts(eventType = 'widget_open', year, month) {
        try {
            const params = new URLSearchParams({ event_type: eventType });
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(
                buildUrl(`/api/events/daily?${params}`),
                { method: 'GET', credentials: 'include' }
            );
            if (!response.ok) throw new Error('Failed to fetch daily event counts');
            return await response.json();
        } catch (error) {
            return [];
        }
    },

    async getDailyConversationCounts(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(
                buildUrl(`/api/events/daily-conversations?${params}`),
                { method: 'GET', credentials: 'include' }
            );
            if (!response.ok) throw new Error('Failed to fetch daily conversation counts');
            return await response.json();
        } catch (error) {
            return [];
        }
    },

    async getTopPages(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/events/top-pages?${params}`), {
                method: 'GET', credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch top pages');
            return await response.json();
        } catch (error) {
            return [];
        }
    },

    async getTopSources(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/events/top-sources?${params}`), {
                method: 'GET', credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch top sources');
            return await response.json();
        } catch (error) {
            return [];
        }
    },

    async getAvgMessagesPerConversation(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/events/avg-messages?${params}`), {
                method: 'GET', credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch avg messages');
            return await response.json();
        } catch (error) {
            return { avg_messages: 0 };
        }
    },

    async getBusiestHours(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/events/busiest-hours?${params}`), {
                method: 'GET', credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch busiest hours');
            return await response.json();
        } catch (error) {
            return [];
        }
    },

    async getMonthlyConversationStats(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/conversation/monthly-stats?${params}`), {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch monthly conversation stats');
            return await response.json();
        } catch (error) {
            return null;
        }
    },

    async getAllConversations({ limit = 50, offset = 0 } = {}) {
        try {
            const params = new URLSearchParams({ limit, offset });
            const response = await fetch(buildUrl(`/api/conversations/all?${params}`), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Returns { conversations: [...], total_count: N }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getAdminConversationDetail(conversationId) {
        const response = await fetch(buildUrl(`/api/conversations/${conversationId}`), {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async uploadFile(formData) {
        try {
            const response = await fetch(buildUrl('/api/upload'), {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed: ${response.status === 404 ? 'File not found' : errorText}`);
            }
            
            return response.json();
        } catch (error) {
            throw error;
        }
    },

    async listFiles(folderPath = '') {
        const params = new URLSearchParams();
        if (folderPath) {
            params.append('folder', folderPath);
        }
        
        const response = await fetch(buildUrl(`/api/files?${params}`), {
            credentials: 'include'
        });
        
        if (!response.ok) {
            throw new Error(`Failed to list files: ${response.statusText}`);
        }
        
        return response.json();
    },

    /**
     * Returns the full URL to stream/download a file by its ID.
     * The caller must fetch this URL with `credentials: 'include'` because
     * the endpoint is auth-protected and iframes cannot send cookies on their own.
     *
     * file_id is now the tenant-relative path (e.g. ``my-product/foo.pdf``),
     * so we encode each path segment individually — that way slashes are kept
     * as path separators (Flask's ``<path:>`` converter captures them) while
     * umlauts / spaces / special chars in filenames get properly percent-encoded.
     */
    getFileUrl(fileId) {
        return buildUrl(`/api/files/${encodeFileId(fileId)}`);
    },

    /** Preview/download URL for a customer chat attachment (chat-attachments/...). */
    getChatAttachmentUrl(s3Key, tenantSlug) {
        const slug = tenantSlug || resolveTenantSlugForApi();
        if (!slug || !s3Key) {
            return null;
        }
        const encodedKey = encodeFileId(s3Key);
        return buildUrl(
            `/api/chat/attachments/${encodeURIComponent(slug)}/${encodedKey}`,
        );
    },

    /**
     * Upload one customer chat attachment (image/PDF) before sending a message.
     * Returns { s3_key, filename, content_type, size }.
     */
    async uploadChatAttachment(file) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(buildUrl(config.endpoints.chatAttachments), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                ...sourceHeader(),
                ...pageUrlHeader(),
                ...pageLanguageHeader(),
                ...anonymousIdHeader(),
            },
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `Upload failed (${response.status})`);
        }

        const data = await response.json();
        if (data.user_id) setAnonymousId(data.user_id);
        return data;
    },

    async deleteFile(fileId) {
        const response = await fetch(buildUrl(`/api/files/${encodeFileId(fileId)}`), {
            method: 'DELETE',
            credentials: 'include'
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to delete file: ${response.status === 404 ? 'File not found' : errorText}`);
        }
        
        return response.json();
    },

    async createFolder(data) {
        const response = await fetch(buildUrl('/api/folders'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data)
        });
        
        if (!response.ok) {
            throw new Error(`Failed to create folder: ${response.statusText}`);
        }
        
        return response.json();
    },

    async moveFile(fileId, destinationFolder) {
        const response = await fetch(buildUrl(`/api/files/${encodeFileId(fileId)}/move`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ destination_folder: destinationFolder })
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to move file: ${response.status === 404 ? 'File not found' : errorText}`);
        }
        
        return response.json();
    },

    async updateFileSettings(filePath, showAsSource) {
        const response = await fetch(buildUrl('/api/files/settings'), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ file_path: filePath, show_as_source: showAsSource }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update file settings');
        }
        return response.json();
    },

    async deleteFolder(folderPath) {
        const response = await fetch(buildUrl(`/api/folders/${folderPath}`), {
            method: 'DELETE',
            credentials: 'include'
        });
        
        if (!response.ok) {
            throw new Error(`Failed to delete folder: ${response.statusText}`);
        }
        
        return response.json();
    },

    // ── Product management ─────────────────────────────────────────

    async listProducts() {
        const response = await fetch(buildUrl('/api/products'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load products');
        return response.json();
    },

    async createProduct(data) {
        const response = await fetch(buildUrl('/api/products'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to create product');
        }
        return response.json();
    },

    async updateProduct(productId, data) {
        const response = await fetch(buildUrl(`/api/products/${productId}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update product');
        }
        return response.json();
    },

    async generateProductDemoQuestions(productId) {
        const response = await fetch(buildUrl(`/api/products/${productId}/generate-demo-questions`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to generate demo questions');
        }
        return response.json();
    },

    async deleteProduct(productId) {
        const response = await fetch(buildUrl(`/api/products/${productId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to delete product');
        return response.json();
    },

    // ── MCP tokens (Admin Settings → MCP section) ───────────────────

    async listMcpTools() {
        const response = await fetch(buildUrl('/api/mcp/tools'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load MCP tools');
        return response.json();
    },

    async listMcpTokens() {
        const response = await fetch(buildUrl('/api/mcp/tokens'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load MCP tokens');
        return response.json();
    },

    // Returns the created token INCLUDING the one-time plaintext `token` field —
    // the backend never returns it again, so the UI must show it immediately.
    // Backend always grants full MCP scopes (no scope picker).
    async createMcpToken(name) {
        const response = await fetch(buildUrl('/api/mcp/tokens'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to create MCP token');
        }
        return response.json();
    },

    async revokeMcpToken(tokenId) {
        const response = await fetch(buildUrl(`/api/mcp/tokens/${tokenId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to revoke MCP token');
        }
        return response.json();
    },

    // Re-view a token plaintext after password re-auth (eye icon in token list).
    async revealMcpToken(tokenId, password) {
        const response = await fetch(buildUrl(`/api/mcp/tokens/${tokenId}/reveal`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ password }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to reveal MCP token');
        }
        return response.json();
    },

    async listMcpOAuthClients() {
        const response = await fetch(buildUrl('/api/mcp/oauth-clients'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load MCP OAuth clients');
        return response.json();
    },

    async revokeMcpOAuthClient(clientId) {
        const response = await fetch(buildUrl(`/api/mcp/oauth-clients/${clientId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to revoke OAuth client');
        }
        return response.json();
    },

    async getProductImportJobStatus(jobId) {
        const response = await fetch(buildUrl(`/api/products/import/jobs/${jobId}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to get import status');
        }
        return response.json();
    },

    // ── Sitemap-connected knowledge (Connected Knowledge page) ─────

    async discoverSitemapKnowledge(sitemapUrl, urlFilter = '') {
        const response = await fetch(buildUrl('/api/sitemap-knowledge/discover'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                sitemap_url: sitemapUrl,
                url_filter: urlFilter,
            }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to discover sitemap URLs');
        }
        return response.json();
    },

    async listSitemapKnowledgeSources() {
        const response = await fetch(buildUrl('/api/sitemap-knowledge/sources'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to list sitemap sources');
        }
        return response.json();
    },

    async createSitemapKnowledgeSource({ sitemapUrl, urls, urlFilter = '', label = '', lastmod = {} }) {
        const response = await fetch(buildUrl('/api/sitemap-knowledge/sources'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                sitemap_url: sitemapUrl,
                urls,
                url_filter: urlFilter,
                label,
                lastmod,
            }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to create sitemap source');
        }
        return response.json();
    },

    async getSitemapKnowledgeSource(sourceId) {
        const response = await fetch(buildUrl(`/api/sitemap-knowledge/sources/${sourceId}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to load sitemap source');
        }
        return response.json();
    },

    async deleteSitemapKnowledgeSource(sourceId) {
        const response = await fetch(buildUrl(`/api/sitemap-knowledge/sources/${sourceId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to delete sitemap source');
        }
        return response.json();
    },

    async setSitemapKnowledgePageEnabled(pageId, enabled) {
        const response = await fetch(buildUrl(`/api/sitemap-knowledge/pages/${pageId}`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ enabled }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update page');
        }
        return response.json();
    },

    async getSitemapKnowledgeJobStatus(jobId) {
        const response = await fetch(buildUrl(`/api/sitemap-knowledge/jobs/${jobId}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to get job status');
        }
        return response.json();
    },

    // ── Language management (i18n) ─────────────────────────────────

    // Kick off the add-language bootstrap (auto-translate FAQ, welcome
    // message, demo questions, portal copy, return reasons; warm catalogs).
    // Returns 202 with {status: 'started'} — poll getLanguageBootstrapStatus.
    async startLanguageBootstrap(language) {
        const response = await fetch(buildUrl('/api/i18n/bootstrap'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ language }),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Failed to start language bootstrap');
        }
        return data;
    },

    async getLanguageBootstrapStatus() {
        const response = await fetch(buildUrl('/api/i18n/bootstrap/status'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch bootstrap status');
        return response.json();
    },

    // Resolved system string catalog (widget/portal/…) for a language —
    // used by admin editors to show translated defaults when the tenant
    // has not customized contact_form_copy_{lang} etc. yet.
    async getI18nStrings(namespace, lang) {
        const params = new URLSearchParams({ ns: namespace, lang });
        const response = await fetch(buildUrl(`/api/i18n/strings?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch i18n strings');
        return response.json();
    },

    // ── Branding management ────────────────────────────────────────

    async getBranding() {
        const response = await fetch(buildUrl(config.endpoints.branding), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch branding');
        return response.json();
    },

    async updateBranding(data) {
        const response = await fetch(buildUrl(config.endpoints.branding), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update branding');
        }
        return response.json();
    },

    async uploadBrandingAsset(file, assetType) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', assetType);

        const response = await fetch(buildUrl(config.endpoints.brandingUpload), {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to upload branding asset');
        }
        return response.json();
    },

    // ── Lead status update ──────────────────────────────────────────

    async updateLeadStatus(leadId, status) {
        const response = await fetch(`${config.api.baseUrl}${config.endpoints.leads}/${leadId}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ status }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update lead status');
        }
        return response.json();
    },

    // ── Admin Rules CRUD ─────────────────────────────────────────────

    // Optional filters: { status: 'proposed'|'active'|'rejected'|'expired', origin: 'learned'|'curated' }
    async getAdminRules({ status, origin } = {}) {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (origin) params.set('origin', origin);
        const qs = params.toString();
        const response = await fetch(buildUrl(`/api/admin/rules${qs ? `?${qs}` : ''}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch rules');
        return response.json();
    },

    // ── Reply learning (Knowledge → Auto-Learning) ─────────────────────────

    async _rulesPost(path, body = {}) {
        const response = await fetch(buildUrl(path), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Request failed');
        return data;
    },

    // Approve a learned proposal; optional { title, rule_text, note, replace_conflicting }
    async approveRule(ruleId, payload = {}) {
        return chatService._rulesPost(`/api/admin/rules/${ruleId}/approve`, payload);
    },

    async rejectRule(ruleId, note) {
        return chatService._rulesPost(`/api/admin/rules/${ruleId}/reject`, note ? { note } : {});
    },

    // action: 'approve' | 'reject'
    async bulkReviewRules(ruleIds, action, note) {
        return chatService._rulesPost('/api/admin/rules/bulk-review', {
            rule_ids: ruleIds, action, ...(note ? { note } : {}),
        });
    },

    async getRuleAuditLog(ruleId) {
        const response = await fetch(buildUrl(`/api/admin/rules/${ruleId}/audit-log`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load rule history');
        return response.json();
    },

    async getRuleEvidence(ruleId) {
        const response = await fetch(buildUrl(`/api/admin/rules/${ruleId}/evidence`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load rule evidence');
        return response.json();
    },

    async getReplyLearningSummary() {
        const response = await fetch(buildUrl('/api/admin/reply-learning/summary'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load learning summary');
        return response.json();
    },

    async getReplyLearningEvents({ status, limit = 50, offset = 0 } = {}) {
        const params = new URLSearchParams({ limit, offset });
        if (status) params.set('status', status);
        const response = await fetch(buildUrl(`/api/admin/reply-learning/events?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load learning activity');
        return response.json();
    },

    // ── General Knowledge audit trail ───────────────────────────────

    async getKnowledgeAuditLog({ filePath, limit = 50, offset = 0 } = {}) {
        const params = new URLSearchParams({ limit, offset });
        if (filePath) params.set('file_path', filePath);
        const response = await fetch(buildUrl(`/api/knowledge-audit-log?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load knowledge history');
        return response.json();
    },

    async getKnowledgeAuditDiff(entryId) {
        const response = await fetch(buildUrl(`/api/knowledge-audit-log/${entryId}/diff`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load change details');
        return response.json();
    },

    async createAdminRule(data) {
        const response = await fetch(buildUrl('/api/admin/rules'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to create rule');
        }
        return response.json();
    },

    // data: { title?, rule_text?, edit_note? } — audited server-side
    async updateAdminRule(ruleId, data) {
        const response = await fetch(buildUrl(`/api/admin/rules/${ruleId}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to update rule');
        }
        return response.json();
    },

    async deleteAdminRule(ruleId) {
        const response = await fetch(buildUrl(`/api/admin/rules/${ruleId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to delete rule');
        return response.json();
    },

    async toggleAdminRule(ruleId, isActive) {
        const response = await fetch(buildUrl(`/api/admin/rules/${ruleId}/toggle`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ is_active: isActive }),
        });
        if (!response.ok) throw new Error('Failed to toggle rule');
        return response.json();
    },

    // ── Feedback inbox (admin) ───────────────────────────────────────

    async getFeedbackKnowledgeCounts() {
        const response = await fetch(buildUrl('/api/admin/feedback/knowledge-counts'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch feedback knowledge counts');
        return response.json();
    },

    async getFeedbackInbox(status = 'all', limit = 50, offset = 0) {
        const params = new URLSearchParams({ status, limit, offset });
        const response = await fetch(buildUrl(`/api/admin/feedback/inbox?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch feedback inbox');
        return response.json();
    },

    async updateFeedbackReview(feedbackId, status) {
        const response = await fetch(buildUrl(`/api/admin/feedback/${feedbackId}/review`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ status }),
        });
        if (!response.ok) throw new Error('Failed to update feedback review');
        return response.json();
    },

    // Edit a feedback item's text/polarity with an optional note
    async editFeedback(feedbackId, { feedback_text, is_positive, edit_note }) {
        const response = await fetch(buildUrl(`/api/admin/feedback/${feedbackId}/edit`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ feedback_text, is_positive, edit_note }),
        });
        if (!response.ok) throw new Error('Failed to edit feedback');
        return response.json();
    },

    // Get the edit history log for a feedback item
    async getFeedbackEditLog(feedbackId) {
        const response = await fetch(buildUrl(`/api/admin/feedback/${feedbackId}/edit-log`), {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to get feedback edit log');
        return response.json();
    },

    // ── FAQ module ───────────────────────────────────────────────────

    // Public: published FAQ items + layout/title for the embeddable block.
    // Optional language code — the backend serves that language's items (with a
    // fallback to the tenant default when the language has none).
    async getPublicFaq(lang = null, tag = null) {
        const params = new URLSearchParams();
        if (lang) params.set('lang', lang);
        if (tag) params.set('tag', tag);
        const qs = params.toString();
        const url = qs ? `/api/faq?${qs}` : '/api/faq';
        const response = await fetch(buildUrl(url), { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch FAQ');
        return response.json();
    },

    // Admin: list every FAQ item (all statuses, all languages) for review.
    // Optional language filter.
    async getAdminFaq(lang = null) {
        const url = lang ? `/api/admin/faq?lang=${encodeURIComponent(lang)}` : '/api/admin/faq';
        const response = await fetch(buildUrl(url), { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch FAQ items');
        return response.json();
    },

    async createFaqItem(data) {
        const response = await fetch(buildUrl('/api/admin/faq'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to create FAQ item');
        }
        return response.json();
    },

    async updateFaqItem(itemId, data) {
        const response = await fetch(buildUrl(`/api/admin/faq/${itemId}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update FAQ item');
        return response.json();
    },

    // Set tags on every language version in a translation group at once.
    async updateFaqGroupTags(translationGroup, tags) {
        const response = await fetch(buildUrl(`/api/admin/faq/group/${translationGroup}/tags`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ tags }),
        });
        if (!response.ok) throw new Error('Failed to update FAQ tags');
        return response.json();
    },

    async deleteFaqItem(itemId) {
        const response = await fetch(buildUrl(`/api/admin/faq/${itemId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to delete FAQ item');
        return response.json();
    },

    async reorderFaq(orderedIds) {
        const response = await fetch(buildUrl('/api/admin/faq/reorder'), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ordered_ids: orderedIds }),
        });
        if (!response.ok) throw new Error('Failed to reorder FAQ');
        return response.json();
    },

    // Trigger a manual regeneration from recent conversations (synchronous).
    async regenerateFaq() {
        const response = await fetch(buildUrl('/api/admin/faq/regenerate'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to regenerate FAQ');
        return response.json();
    },

    // ── Support System (tickets) ─────────────────────────────────────

    async getTickets(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') params.append(key, value);
        });
        const response = await fetch(buildUrl(`/api/support/tickets?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch tickets');
        return response.json();
    },

    async refreshSupportTickets() {
        const response = await fetch(buildUrl('/api/support/tickets/refresh'), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to refresh tickets');
        return response.json();
    },

    /** Lightweight poll — revision changes when the inbox has new/closed/updated tickets. */
    async getTicketInboxSync(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') params.append(key, value);
        });
        const response = await fetch(buildUrl(`/api/support/tickets/sync?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to sync tickets');
        return response.json();
    },

    async getTicket(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch ticket');
        return response.json();
    },

    async heartbeatTicketPresence(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/presence`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to heartbeat ticket presence');
        return response.json();
    },

    async leaveTicketPresence(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/presence`), {
            method: 'DELETE',
            credentials: 'include',
            keepalive: true,
        });
        if (!response.ok) throw new Error('Failed to leave ticket presence');
        return response.json();
    },

    async getTicketPresence(conversationIds = []) {
        const ids = (conversationIds || []).filter(Boolean);
        if (!ids.length) return { presence: {} };
        const params = new URLSearchParams({ conversation_ids: ids.join(',') });
        const response = await fetch(buildUrl(`/api/support/tickets/presence?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch ticket presence');
        return response.json();
    },

    /** Address change modal data: order, current address, eligibility, suggestions. */
    async getShopifyAddressChangePreview(conversationId, orderNumber = '') {
        const params = new URLSearchParams();
        if (orderNumber) params.append('order_number', orderNumber);
        const response = await fetch(
            buildUrl(`/api/support/tickets/${conversationId}/shopify/address-change?${params}`),
            { credentials: 'include' },
        );
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load address change preview');
        }
        return response.json();
    },

    /** Write the admin-edited shipping address to the Shopify order. */
    async applyShopifyAddressChange(conversationId, { orderNumber, address, ignorePolicies = false }) {
        const response = await fetch(
            buildUrl(`/api/support/tickets/${conversationId}/shopify/address-change`),
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    order_number: orderNumber,
                    address,
                    ignore_policies: ignorePolicies,
                }),
            },
        );
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to apply address change');
        }
        return response.json();
    },

    /** Admin correction of the ticket's customer email (profile + mail reply recipient). */
    async updateTicketCustomerEmail(conversationId, email) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/customer-email`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update customer email');
        }
        return response.json();
    },

    async updateTicket(conversationId, data) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update ticket');
        }
        return response.json();
    },

    /**
     * Apply one status change to many tickets at once (bulk close from the list).
     * Resolves with { updated: [ticket], failed: [{ id, error }] } — partial
     * success is a normal 200 response, so callers must inspect `failed`.
     */
    async bulkUpdateTickets(conversationIds, data) {
        const response = await fetch(buildUrl('/api/support/tickets/bulk'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ...data, ids: conversationIds }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update tickets');
        }
        return response.json();
    },

    async replyToTicket(conversationId, text, { connectionId, attachments, draftId } = {}) {
        const body = { text };
        if (connectionId) body.connection_id = connectionId;
        if (attachments !== undefined) body.attachments = attachments;
        // AI draft session the text was edited from — lets reply learning
        // compare draft vs. sent reply.
        if (draftId) body.draft_id = draftId;
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/reply`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to send reply');
        }
        return response.json();
    },

    async addTicketNote(conversationId, text) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/note`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ text }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to add note');
        }
        return response.json();
    },

    async startShopifyReturn(conversationId, { reason, orderNumber, email, ignorePolicies } = {}) {
        const body = {};
        if (reason) body.reason = reason;
        if (orderNumber) body.order_number = orderNumber;
        if (email) body.email = email;
        if (ignorePolicies) body.ignore_policies = true;
        const response = await fetch(
            buildUrl(`/api/support/tickets/${conversationId}/shopify/start-return`),
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || 'Failed to start Shopify return');
        }
        return data;
    },

    // ── Claims admin (Reklamationsportal review) ─────────────────────

    /** Newest claim behind a ticket conversation — null when none exists. */
    async getClaimByConversation(conversationId) {
        const response = await fetch(
            buildUrl(`/api/admin/claims/by-conversation/${conversationId}`),
            { credentials: 'include' },
        );
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to fetch claim');
        }
        return response.json();
    },

    /** Shared POST for claim decisions (approve / adjust / deny). */
    async _claimDecision(claimId, action, body = {}) {
        const response = await fetch(buildUrl(`/api/admin/claims/${claimId}/${action}`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || `Claim ${action} failed`);
        }
        return data;
    },

    async approveClaim(claimId) {
        return chatService._claimDecision(claimId, 'approve');
    },

    async adjustClaim(claimId, resolution) {
        return chatService._claimDecision(claimId, 'adjust', { resolution });
    },

    async denyClaim(claimId, reason) {
        return chatService._claimDecision(claimId, 'deny', { reason });
    },

    async draftTicketReply(conversationId, { currentDraft, instructions, draftId } = {}) {
        const body = {};
        if (currentDraft) body.current_draft = currentDraft;
        if (instructions) body.instructions = instructions;
        if (draftId) body.draft_id = draftId;
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/draft-reply`), {
            method: 'POST',
            headers: Object.keys(body).length ? { 'Content-Type': 'application/json' } : undefined,
            credentials: 'include',
            body: Object.keys(body).length ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to draft reply');
        }
        return response.json();
    },

    async convertTicketToFaq(conversationId, question, answer) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/convert-to-faq`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ question, answer }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to convert to FAQ');
        }
        return response.json();
    },

    async getSupportStats(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);
            const response = await fetch(buildUrl(`/api/support/stats?${params}`), {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to fetch support stats');
            return await response.json();
        } catch (error) {
            return null;
        }
    },

    // ── Task Board ───────────────────────────────────────────────────

    async listTaskBoardTasks(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') return;
            params.set(k, String(v));
        });
        const qs = params.toString();
        const response = await fetch(buildUrl(`/api/task-board${qs ? `?${qs}` : ''}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to list tasks');
        }
        return response.json();
    },

    /** Tenant-defined Signals attributes (e.g. platform: iOS / Android). */
    async getTaskBoardAttributes() {
        const response = await fetch(buildUrl('/api/task-board/attributes'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load attributes');
        }
        return response.json();
    },

    async saveTaskBoardAttributes(attributes) {
        const response = await fetch(buildUrl('/api/task-board/attributes'), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ attributes }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to save attributes');
        }
        return response.json();
    },

    /** Background AI tagging of existing cards (fills missing values only). */
    async backfillTaskBoardAttributes(includeDone = false) {
        const response = await fetch(buildUrl('/api/task-board/attributes/backfill'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ include_done: includeDone }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to start classification');
        }
        return response.json();
    },

    async listTaskBoardAssignees() {
        const response = await fetch(buildUrl('/api/task-board/assignees'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load assignees');
        }
        return response.json();
    },

    async getTaskBoardTask(taskId) {
        const response = await fetch(buildUrl(`/api/task-board/${taskId}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load task');
        }
        return response.json();
    },

    async createTaskBoardTask(data) {
        const response = await fetch(buildUrl('/api/task-board'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to create task');
        }
        return response.json();
    },

    async updateTaskBoardTask(taskId, data) {
        const response = await fetch(buildUrl(`/api/task-board/${taskId}`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update task');
        }
        return response.json();
    },

    async moveTaskBoardTask(taskId, data) {
        const response = await fetch(buildUrl(`/api/task-board/${taskId}/move`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to move task');
        }
        return response.json();
    },

    async commentTaskBoardTask(taskId, text) {
        const response = await fetch(buildUrl(`/api/task-board/${taskId}/comment`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ text }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to add comment');
        }
        return response.json();
    },

    async deleteTaskBoardTask(taskId) {
        const response = await fetch(buildUrl(`/api/task-board/${taskId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to delete task');
        }
        return response.json();
    },

    async promoteConversationToTask(conversationId, extras = {}) {
        const response = await fetch(buildUrl('/api/task-board/promote'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ conversation_id: conversationId, ...extras }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to promote conversation');
        }
        return response.json();
    },

    async findSimilarTaskBoardTasks(conversationId) {
        const params = new URLSearchParams({ conversation_id: conversationId });
        const response = await fetch(buildUrl(`/api/task-board/similar?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to find similar tasks');
        }
        return response.json();
    },

    async linkConversationToTask({ taskId, conversationId, linkReason }) {
        const response = await fetch(buildUrl('/api/task-board/link'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                task_id: taskId,
                conversation_id: conversationId,
                link_reason: linkReason,
            }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to link conversation');
        }
        return response.json();
    },

    async getSupportCategories() {
        try {
            const response = await fetch(buildUrl('/api/support/categories'), {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to fetch categories');
            const data = await response.json();
            return data.categories || [];
        } catch (error) {
            return [];
        }
    },

    // ── Mail Connector (Gmail / Outlook) ─────────────────────────────

    async getMailConnections() {
        const response = await fetch(buildUrl('/api/admin/mail/connections'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load mail connections');
        const data = await response.json();
        return data.connections || [];
    },

    // Returns {url} — the browser must be redirected there for the OAuth consent
    async startMailOAuth(provider) {
        const response = await fetch(buildUrl(`/api/admin/mail/oauth/start/${provider}`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to start OAuth flow');
        }
        return response.json();
    },

    async updateMailConnection(connectionId, settings) {
        const response = await fetch(buildUrl(`/api/admin/mail/connections/${connectionId}`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(settings),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update connection');
        }
        return response.json();
    },

    async disconnectMailConnection(connectionId) {
        const response = await fetch(buildUrl(`/api/admin/mail/connections/${connectionId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to disconnect');
        }
        return response.json();
    },

    async syncMailConnection(connectionId) {
        const response = await fetch(buildUrl(`/api/admin/mail/connections/${connectionId}/sync`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Sync failed');
        }
        return response.json();
    },

    async getMailScreeningLog({ connectionId, status = 'ignored', limit = 50, offset = 0 } = {}) {
        const params = new URLSearchParams({
            status,
            limit: String(limit),
            offset: String(offset),
        });
        if (connectionId) params.set('connection_id', connectionId);
        const response = await fetch(buildUrl(`/api/admin/mail/screening-log?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load mail screening log');
        return response.json();
    },

    async reprocessIgnoredMail(mailMessageId) {
        const response = await fetch(buildUrl(`/api/admin/mail/messages/${mailMessageId}/reprocess`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Reprocess failed');
        }
        return response.json();
    },

    // ── Socials Integration (Instagram DMs + comments) ────────────────

    async getSocialConnections() {
        const response = await fetch(buildUrl('/api/admin/social/connections'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load social connections');
        const data = await response.json();
        return data.connections || [];
    },

    async startSocialOAuth(provider, { returnTo, returnBase } = {}) {
        const params = new URLSearchParams();
        if (returnTo) params.set('return_to', returnTo);
        const origin = returnBase || (typeof window !== 'undefined' ? window.location.origin : undefined);
        if (origin) params.set('return_base', origin);
        const qs = params.toString();
        const response = await fetch(buildUrl(
            qs ? `/api/admin/social/oauth/start/${provider}?${qs}` : `/api/admin/social/oauth/start/${provider}`,
        ), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to start OAuth flow');
        }
        return response.json();
    },

    async updateSocialConnection(connectionId, settings) {
        const response = await fetch(buildUrl(`/api/admin/social/connections/${connectionId}`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(settings),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update connection');
        }
        return response.json();
    },

    async disconnectSocialConnection(connectionId) {
        const response = await fetch(buildUrl(`/api/admin/social/connections/${connectionId}`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to disconnect');
        }
        return response.json();
    },

    async syncSocialConnection(connectionId) {
        const response = await fetch(buildUrl(`/api/admin/social/connections/${connectionId}/sync`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Sync failed');
        }
        return response.json();
    },

    async getSocialScreeningLog({ connectionId, status = 'all', limit = 50, offset = 0 } = {}) {
        const params = new URLSearchParams({
            status,
            limit: String(limit),
            offset: String(offset),
        });
        if (connectionId) params.set('connection_id', connectionId);
        const response = await fetch(buildUrl(`/api/admin/social/screening-log?${params}`), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load social screening log');
        return response.json();
    },

    async reprocessSocialMessage(socialMessageId) {
        const response = await fetch(buildUrl(`/api/admin/social/messages/${socialMessageId}/reprocess`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Reprocess failed');
        }
        return response.json();
    },

    async discardSocialDraft(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/social-draft/discard`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to discard draft');
        }
        return response.json();
    },

    async discardMailDraft(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/mail-draft/discard`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to discard draft');
        }
        return response.json();
    },

    async uploadMailAttachment(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(buildUrl('/api/support/mail/attachments'), {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to upload attachment');
        }
        return response.json();
    },

    async deleteMailAttachment(s3Key) {
        const response = await fetch(buildUrl('/api/support/mail/attachments'), {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3_key: s3Key }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to delete attachment');
        }
        return response.json();
    },

    // scope: 'domain' (everyone @domain) or 'address' (exact email only)
    async ignoreMailSenderDomain(conversationId, { scope = 'domain' } = {}) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/ignore-sender-domain`), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scope }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to ignore sender');
        }
        return response.json();
    },

    async ignoreSocialSender(conversationId) {
        const response = await fetch(buildUrl(`/api/support/tickets/${conversationId}/ignore-social-sender`), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to ignore Instagram user');
        }
        return response.json();
    },

    getInboundMailAttachmentUrl(mailMessageId, filename) {
        const encodedName = encodeURIComponent(filename);
        return buildUrl(
            `/api/support/mail/inbound-attachment/${mailMessageId}/${encodedName}`,
        );
    },

    // ── Klaviyo integration (email_notifications module) ─────────────

    // Returns {connection, events} — connection is {connected:false} or the
    // masked account info (never the API key itself)
    async getKlaviyoConnection() {
        const response = await fetch(buildUrl('/api/admin/klaviyo/connection'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load Klaviyo connection');
        return response.json();
    },

    async connectKlaviyo(apiKey) {
        const response = await fetch(buildUrl('/api/admin/klaviyo/connection'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ api_key: apiKey }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to connect Klaviyo');
        }
        return response.json();
    },

    async disconnectKlaviyo() {
        const response = await fetch(buildUrl('/api/admin/klaviyo/connection'), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to disconnect Klaviyo');
        }
        return response.json();
    },

    // Metric-triggered flows of the connected account (for the event→flow mapping)
    async getKlaviyoFlows() {
        const response = await fetch(buildUrl('/api/admin/klaviyo/flows'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load Klaviyo flows');
        }
        const data = await response.json();
        return data.flows || [];
    },

    async getKlaviyoLists() {
        const response = await fetch(buildUrl('/api/admin/klaviyo/lists'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load Klaviyo lists');
        }
        const data = await response.json();
        return data.lists || [];
    },

    async sendKlaviyoTest(email, event) {
        const response = await fetch(buildUrl('/api/admin/klaviyo/test'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, event }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Test failed');
        }
        return response.json();
    },

    // ── Slack notifications module ───────────────────────────────────

    async getSlackEvents() {
        const response = await fetch(buildUrl('/api/support/slack/events'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch Slack events');
        return response.json();
    },

    async testSlackNotification() {
        const response = await fetch(buildUrl('/api/support/slack/test'), {
            method: 'POST',
            credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Slack test failed');
        return data;
    },

    // ── Microsoft Teams internal notifications ───────────────────────

    async getTeamsConnection() {
        const response = await fetch(buildUrl('/api/admin/teams/connection'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to load Teams connection');
        return response.json();
    },

    async startTeamsOAuth() {
        const response = await fetch(buildUrl('/api/admin/teams/oauth/start'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to start Teams OAuth');
        }
        return response.json();
    },

    async disconnectTeams() {
        const response = await fetch(buildUrl('/api/admin/teams/connection'), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to disconnect Teams');
        }
        return response.json();
    },

    async updateTeamsDefaultChannel(payload) {
        const response = await fetch(buildUrl('/api/admin/teams/connection/default-channel'), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to save default channel');
        }
        return response.json();
    },

    async listTeamsJoinedTeams() {
        const response = await fetch(buildUrl('/api/admin/teams/teams'), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to list teams');
        }
        return response.json();
    },

    async listTeamsChannels(teamId) {
        const response = await fetch(buildUrl(`/api/admin/teams/teams/${encodeURIComponent(teamId)}/channels`), {
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to list channels');
        }
        return response.json();
    },

    async getTeamsEvents() {
        const response = await fetch(buildUrl('/api/support/teams/events'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch Teams events');
        return response.json();
    },

    async testTeamsNotification() {
        const response = await fetch(buildUrl('/api/support/teams/test'), {
            method: 'POST',
            credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Teams test failed');
        return data;
    },

    // ── Email notifications module ───────────────────────────────────

    async testEmailNotification(recipient) {
        const response = await fetch(buildUrl('/api/support/email/test'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ recipient }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Email test failed');
        return data;
    },

    /** Test DHL OAuth (Developer Portal + GKP) without creating a label. */
    async testDhlConnection(credentials = {}) {
        const response = await fetch(buildUrl('/api/admin/dhl/test'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(credentials),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const msg = [data.error, data.hint].filter(Boolean).join(' — ');
            throw new Error(msg || 'DHL connection test failed');
        }
        return data;
    },

    /** Probe DHL Unified Tracking with one shipment number. */
    async testDhlTracking({ trackingNumber, postalCode } = {}) {
        const response = await fetch(buildUrl('/api/admin/dhl/track-test'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                tracking_number: trackingNumber,
                postal_code: postalCode || '',
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || data.description || 'DHL tracking test failed');
        }
        return data;
    },

    // Public: exchange a magic-link token for conversation access (sets cookies)
    async resolveMagicLink(token) {
        const response = await fetch(buildUrl(`/api/support/magic-link/${token}`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Invalid or expired link');
        }
        return response.json();
    },

    // ── Return portal embed (public) ─────────────────────────────────

    async getReturnPortalConfig() {
        const response = await fetch(buildUrl('/api/returns/portal/config'), {
            headers: {
                Accept: 'application/json',
                ...pageLanguageHeader(),
            },
            credentials: 'include',
        });
        if (!response.ok) throw new Error(`Return portal config failed (${response.status})`);
        return response.json();
    },

    async returnPortalLookup(orderNumber, email) {
        const response = await fetch(buildUrl('/api/returns/portal/lookup'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...sourceHeader(),
                ...pageUrlHeader(),
                ...pageLanguageHeader(),
            },
            credentials: 'include',
            body: JSON.stringify({ order_number: orderNumber, email }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
            const err = new Error(payload.message || payload.error || 'Lookup failed');
            err.payload = payload;
            throw err;
        }
        return payload;
    },

    async returnPortalLookupStream(orderNumber, email, callbacks = {}) {
        const { onAckDone, onThinking, onDone, onError, signal } = callbacks;

        try {
            const response = await fetch(buildUrl('/api/returns/portal/lookup/stream'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    ...sourceHeader(),
                    ...pageUrlHeader(),
                    ...pageLanguageHeader(),
                },
                credentials: 'include',
                body: JSON.stringify({ order_number: orderNumber, email }),
                signal,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let receivedDone = false;
            let result = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');
                buffer = events.pop();

                for (const eventBlock of events) {
                    if (!eventBlock.trim()) continue;

                    let eventType = null;
                    let eventData = null;
                    const lines = eventBlock.split('\n');
                    for (const line of lines) {
                        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                        else if (line.startsWith('data: ')) eventData = line.slice(6);
                    }

                    if (!eventType || !eventData) continue;

                    try {
                        const parsedData = JSON.parse(eventData);
                        switch (eventType) {
                            case 'ack_done':
                                if (onAckDone) onAckDone(parsedData);
                                break;
                            case 'thinking':
                                if (onThinking) onThinking(parsedData);
                                break;
                            case 'done':
                                receivedDone = true;
                                result = parsedData;
                                if (onDone) await onDone(parsedData);
                                break;
                            case 'error': {
                                const err = new Error(parsedData.error || 'Lookup failed');
                                err.payload = parsedData.payload;
                                if (onError) onError(err);
                                else throw err;
                                break;
                            }
                            default:
                                break;
                        }
                    } catch (e) {
                        if (eventType === 'error') throw e;
                        console.error('Error parsing portal lookup SSE:', e);
                    }
                }
            }

            if (!receivedDone) {
                const err = new Error('Stream ended unexpectedly without completion');
                if (onError) onError(err);
                else throw err;
            }
            return result;
        } catch (error) {
            if (onError) onError(error);
            else throw error;
        }
    },

    async returnPortalAction(conversationId, { action, reason_id: reasonId }) {
        const response = await fetch(buildUrl('/api/returns/portal/action'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...sourceHeader(),
                ...pageLanguageHeader(),
            },
            credentials: 'include',
            body: JSON.stringify({
                conversation_id: conversationId,
                action,
                reason_id: reasonId,
            }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
            const err = new Error(payload.error || 'Action failed');
            err.payload = payload;
            throw err;
        }
        return payload;
    },

    // ── Onboarding checklist (admin) ─────────────────────────────────

    async getOnboardingTasks() {
        const response = await fetch(buildUrl('/api/onboarding'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch onboarding tasks');
        return response.json();
    },

    async getOnboardingCount() {
        const response = await fetch(buildUrl('/api/onboarding/count'), {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch onboarding count');
        return response.json();
    },

    async skipOnboardingTask(taskKey) {
        const response = await fetch(buildUrl(`/api/onboarding/${taskKey}/skip`), {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to skip task');
        }
        return response.json();
    },

    async unskipOnboardingTask(taskKey) {
        const response = await fetch(buildUrl(`/api/onboarding/${taskKey}/skip`), {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to un-skip task');
        }
        return response.json();
    },

    // ── User management (tenant admin) ──────────────────────────────

    /** List registered (non-anonymous) users for the current tenant. */
    async listUsers() {
        const response = await fetch(buildUrl('/api/users'), {
            credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to list users');
        return data;
    },

    /** Admin creates a user with a temporary password (forced change on first login). */
    async createUser({ email, password, name }) {
        const response = await fetch(buildUrl('/api/users'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password, name }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to create user');
        return data;
    },

    /** Promote or demote a user (is_admin). Super-admins cannot be changed. */
    async setUserAdmin(userId, isAdmin) {
        const response = await fetch(buildUrl(`/api/users/${userId}/admin`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ is_admin: isAdmin }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to update admin status');
        return data;
    },

    /** Activate or deactivate a user (blocks login when inactive). */
    async setUserStatus(userId, isActive) {
        const response = await fetch(buildUrl(`/api/users/${userId}/status`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ is_active: isActive }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to update user status');
        return data;
    },

    /** Admin sets a new password for a user (no email sent). */
    async setUserPassword(userId, password) {
        const response = await fetch(buildUrl(`/api/users/${userId}/password`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ password }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to set password');
        return data;
    },

    /** Delete registered users from the current tenant. */
    async deleteUsers(ids, mode = 'delete_data') {
        const response = await fetch(buildUrl('/api/users'), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids, mode }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to delete users');
        return data;
    },

    // ── Conversation export (admin) ─────────────────────────────────

    /**
     * Download all conversations for a given month as a PDF.
     * Triggers a browser file download.
     */
    async exportConversationsPdf(year, month) {
        const response = await fetch(
            buildUrl(`/api/conversations/export?year=${year}&month=${month}`),
            { credentials: 'include' }
        );
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Export failed');
        }
        // Trigger file download from the PDF blob
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gespraeche_${year}-${String(month).padStart(2, '0')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    },
};